import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';
import { useCanvasData } from '../../contexts/CanvasDataContext';
import { useSelectedChildName } from '../../hooks/useSelectedChildName';
import { getCanonicalChildOrder } from '../../hooks/useChildrenCourseData';
import { getChildColor } from '../../utils/childColors';
import '../../styles/common.css';
import '../../styles/header-improvements.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const { currentUser, logout, availableUsers } = useAuth();
  const { coursesMode, dashboardPath, guestAvailableUsers } = useAppMode();
  // Task 5.8. The header shows the child only when the name is a real match;
  // it never falls back to the sidebar's guess (first child, 'Canvas User').
  const { name: childName, resolved: childResolved, userId: childUserId } = useSelectedChildName();
  const {
    courses,
    clearGuestCredentials,
    clearAllCanvasData
  } = useCanvasData();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Handle scroll effect for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close menus when route changes
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);
  
  // Handle logout for authenticated users
  const handleLogout = async () => {
    try {
      // Clear all Canvas data, state, and cache comprehensively
      clearAllCanvasData();

      // Perform the actual logout (this will clear auth tokens and user state)
      await logout();
      setUserMenuOpen(false);

      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, try to clear local state
      clearAllCanvasData();
      navigate('/login');
    }
  };

  // Handle exit guest mode
  const handleExitGuestMode = async () => {
    // Awaited: clearGuestCredentials also purges the worker's cached guest
    // data (task 5.3c), and navigating first could race it.
    await clearGuestCredentials();
    navigate('/guest');
  };

  // Get initials for avatar
  const getInitials = () => {
    if (coursesMode === 'guest') {
      return 'G'; // Guest mode indicator
    }

    if (currentUser?.email) {
      return currentUser.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };
  
  // Hide header on login/register pages
  const hideHeaderRoutes = ['/login', '/register', '/forgot-password'];
  if (hideHeaderRoutes.includes(location.pathname)) {
    return null;
  }
  
  // Find the current course name if on a course page
  let currentCourseName = null;
  if (location.pathname.startsWith('/course/')) {
    const courseId = location.pathname.split('/')[2];
    const course = courses?.find(c => c.id.toString() === courseId);
    currentCourseName = course?.name;
  }

  // The dot uses the canonical child colour, so it matches every other view.
  // Only drawn when the child is actually in that list: offline and demo have
  // no such list, and a grey fallback dot there would look like data.
  let childDotColor: string | null = null;
  if (childResolved && childUserId && coursesMode !== 'demo' && coursesMode !== 'offline') {
    const canonical = getCanonicalChildOrder(coursesMode === 'guest' ? guestAvailableUsers : availableUsers);
    if (canonical.some((child) => child.id === childUserId)) {
      childDotColor = getChildColor(canonical, childUserId);
    }
  }

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <div className="header-left">
          <div className="logo">
            <Link to={dashboardPath}>
              <span className="logo-icon">📊</span>
              Canvas Dashboard
            </Link>
          </div>

          {childResolved && (
            <>
              <span className="separator">›</span>
              <div className="current-child-indicator">
                {childDotColor && (
                  <span
                    className="current-child-dot"
                    aria-hidden="true"
                    style={{ backgroundColor: childDotColor }}
                  />
                )}
                <span className="current-child-name">{childName}</span>
              </div>
            </>
          )}

          {currentCourseName && (
            <>
              <span className="separator">›</span>
              <div className="current-page-indicator">
                <span className="current-page-title">{currentCourseName}</span>
              </div>
            </>
          )}
        </div>
        
        <div className="header-right">
          {/* User menu for authenticated users */}
          {currentUser && coursesMode !== 'guest' && (
            <div className="user-menu-container">
              <button 
                className="user-avatar" 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                {getInitials()}
              </button>
              
              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-email">{currentUser.email}</div>
                  </div>
                  <div className="user-dropdown-menu">
                    <Link to="/profile" className="user-dropdown-item">
                      <span className="dropdown-item-icon">👤</span>
                      Profile
                    </Link>
                    <button onClick={handleLogout} className="user-dropdown-item logout-item">
                      <span className="dropdown-item-icon">🚪</span>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Guest mode indicator and controls */}
          {coursesMode === 'guest' && (
            <div className="guest-mode-controls">
              <div className="guest-mode-indicator">
                <span className="guest-mode-badge">Guest Mode</span>
              </div>
              <div className="guest-menu-container">
                <button 
                  className="user-avatar guest-avatar" 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  {getInitials()}
                </button>
                
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      <div className="user-dropdown-email">Guest Session</div>
                    </div>
                    <div className="user-dropdown-menu">
                      <Link to="/register" className="user-dropdown-item">
                        <span className="dropdown-item-icon">👤</span>
                        Create Account
                      </Link>
                      <Link to="/login" className="user-dropdown-item">
                        <span className="dropdown-item-icon">🔑</span>
                        Sign In
                      </Link>
                      <button onClick={handleExitGuestMode} className="user-dropdown-item logout-item">
                        <span className="dropdown-item-icon">🚪</span>
                        Exit Guest Mode
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;